import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Clear as ClearIcon,
  Search as SearchIcon,
  WorkOutline as ServiceIcon,
  LocalShippingOutlined as LivrerIcon,
  WarningAmberOutlined as LateIcon,
  PendingActionsOutlined as ResteIcon,
  Inventory2Outlined as MatiereIcon,
} from '@mui/icons-material';
import CustomTablePagination from '../../../components/CustomTablePagination';
import { GlassSurface } from '../../../components/GlassDashboard';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import { useComptoirServices, useComptoirServicesStats } from '../../../hooks/queries/useComptoir';
import { ComptoirService } from '../../../types/comptoir';
import { COLOR, ClientAvatar, EmptyState, ETAT_TONE, PAIEMENT_TONE, StateChip, dh, fDate, fmtPhone } from '../ui';

// as the prototype V8 (etat_com), plus « Livrés en partie »
const ETATS: [string, string][] = [
  ['', 'Tous'],
  ['ouverts', 'En cours (non livrés)'],
  ['avance', 'Avance attendue'],
  ['pret_lancer', 'Prêts à lancer en production'],
  ['prod', 'En production'],
  ['pret', 'Prêts à livrer'],
  ['livre_partiel', 'Livrés en partie'],
  ['livre', 'Livrés'],
  ['cloture', 'Clôturés'],
];
const PAIEMENTS: [string, string][] = [
  ['', 'Tous les paiements'],
  ['attente', "En attente d'avance"],
  ['avance', 'Avance versée'],
  ['partiel', 'Paiement partiel'],
  ['du', 'Livré, reste à régler'],
  ['solde', 'Soldé'],
];

const ComptoirServices: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();  // set by the Tableau de bord cards
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [etat, setEtat] = useState(params.get('etat') || '');
  const [paiement, setPaiement] = useState(params.get('paiement') || '');
  const [enRetard, setEnRetard] = useState(params.get('en_retard') === '1');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const q = useComptoirServices({
    skip: page * rowsPerPage, limit: rowsPerPage, search: debounced || undefined,
    etat: etat || undefined, paiement: paiement || undefined, en_retard: enRetard || undefined,
  });
  const { data: stats } = useComptoirServicesStats();
  const services = q.data?.items ?? [];
  const total = q.data?.total ?? 0;
  const filtered = !!(debounced || etat || paiement || enRetard);

  const kpis = [
    { title: 'Ouverts', value: stats ? String(stats.ouverts) : '…', sub: 'pas encore livrés', icon: <ServiceIcon />, color: COLOR },
    { title: 'À livrer', value: stats ? String(stats.a_livrer) : '…', sub: 'avance reçue', icon: <LivrerIcon />, color: '#7B1FA2' },
    { title: 'En retard', value: stats ? String(stats.en_retard) : '…', sub: 'deadline dépassée', icon: <LateIcon />, color: '#ED6C02' },
    { title: 'Reste à encaisser', value: stats ? dh(stats.reste) : '…', sub: stats ? `${dh(stats.encaisse)} encaissés` : '', icon: <ResteIcon />, color: '#D32F2F' },
  ];

  const row = (s: ComptoirService) => (
    <TableRow key={s.id} hover onClick={() => navigate(`/comptoir/services/${s.id}`)} sx={{ cursor: 'pointer' }}>
      <TableCell>
        <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, color: COLOR, whiteSpace: 'nowrap' }}>{s.reference}</Typography>
        <Typography variant="caption" color="text.secondary">{s.nature}</Typography>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <ClientAvatar name={s.client_nom} size={32} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{s.client_nom}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{fmtPhone(s.client_telephone)}</Typography>
          </Box>
        </Stack>
      </TableCell>
      <TableCell sx={{ maxWidth: 300 }}>
        <Typography variant="body2" noWrap title={s.titre}>{s.titre}</Typography>
        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
          {s.priorite === 'urgente' && s.etat !== 'cloture' && <Chip label="Urgent" size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: 11 }} />}
          {s.has_matiere && <Chip icon={<MatiereIcon />} label="Matière client" size="small" variant="outlined"
            sx={{ height: 20, fontSize: 11, borderColor: COLOR, color: COLOR, '& .MuiChip-icon': { color: COLOR, fontSize: 14 } }} />}
          {s.version > 1 && <Chip label={`v${s.version}`} size="small" sx={{ height: 20, fontSize: 11 }} />}
        </Stack>
      </TableCell>
      <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{s.offre_reference}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap', color: s.en_retard ? 'error.main' : undefined, fontWeight: s.en_retard ? 700 : 400 }}>
        {fDate(s.deadline)}
        {s.en_retard && <Typography variant="caption" display="block" color="error">En retard</Typography>}
      </TableCell>
      <TableCell>
        <StateChip label={s.etat_com_label} t={ETAT_TONE[s.etat_com]} />
        <Typography variant="caption" display="block" color={s.arrets ? 'error' : 'text.secondary'} sx={{ mt: 0.25 }}>
          Production : {s.etat_prod_label}
        </Typography>
        {s.etat === 'livre_partiel' && (
          <LinearProgress variant="determinate" value={s.livre_pct}
            sx={{ mt: 0.75, height: 4, borderRadius: 2, bgcolor: alpha('#7B1FA2', 0.15), '& .MuiLinearProgress-bar': { bgcolor: '#7B1FA2' } }} />
        )}
      </TableCell>
      <TableCell><StateChip label={s.paiement_label} t={PAIEMENT_TONE[s.paiement]} small /></TableCell>
      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap', color: Number(s.reste) > 0 ? 'error.main' : 'success.main' }}>
        {dh(s.reste)}
      </TableCell>
    </TableRow>
  );

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: COLOR }}>Services & Projets</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 800 }}>
          Chaque offre commerciale a son service ou projet (même client, mêmes travaux). Ici : l'avance, les paiements, la livraison et la clôture.
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpis.map((k) => (
          <Grid item xs={12} sm={6} md={3} key={k.title}>
            <GlassSurface accentColor={k.color} sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary">{k.title}</Typography>
                  {stats ? <Typography variant="h5" sx={{ fontWeight: 700 }} noWrap>{k.value}</Typography> : <Skeleton width={100} height={36} />}
                  <Typography variant="caption" color="text.secondary">{k.sub}</Typography>
                </Box>
                <Box sx={{ color: k.color, '& svg': { fontSize: 38 } }}>{k.icon}</Box>
              </Stack>
            </GlassSurface>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ p: 2, pb: 1 }}>
          <TextField
            size="small" placeholder="SRV, PRJ, OFF, client, téléphone, travail…" value={search} onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: { md: 320 } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
              endAdornment: search && <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')} aria-label="Effacer"><ClearIcon fontSize="small" /></IconButton></InputAdornment>,
            }}
          />
          <TextField select size="small" value={etat} onChange={(e) => { setEtat(e.target.value); setPage(0); }} sx={{ minWidth: 200 }} label="État">
            {ETATS.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
          </TextField>
          <TextField select size="small" value={paiement} onChange={(e) => { setPaiement(e.target.value); setPage(0); }} sx={{ minWidth: 200 }} label="Paiement">
            {PAIEMENTS.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
          </TextField>
          <FormControlLabel control={<Checkbox checked={enRetard} onChange={(e) => { setEnRetard(e.target.checked); setPage(0); }} />} label="En retard" />
          <Box sx={{ flex: 1 }} />
          <Typography variant="body2" color="text.secondary">{total} service(s) / projet(s)</Typography>
        </Stack>
        {q.isFetching && <LinearProgress sx={{ height: 2, bgcolor: alpha(COLOR, 0.1), '& .MuiLinearProgress-bar': { bgcolor: COLOR } }} />}
        <Box sx={{ p: 2, pt: 1 }}>
          {!q.isLoading && services.length === 0 ? (
            filtered
              ? <EmptyState icon={<SearchIcon />} title="Aucun service ne correspond" text="Changez la recherche ou les filtres." />
              : <EmptyState icon={<ServiceIcon />} title="Aucun service pour l'instant" text="Un service ou projet est créé automatiquement avec chaque offre commerciale." />
          ) : (
            <>
              <TableContainer sx={getModuleTableTheme('comptoir')}>
                <Table size="small" sx={{ minWidth: 1050 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Réf.</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Désignation</TableCell>
                      <TableCell>Offre</TableCell>
                      <TableCell>Deadline</TableCell>
                      <TableCell>État</TableCell>
                      <TableCell>Paiement</TableCell>
                      <TableCell align="right">Reste</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {q.isLoading
                      ? Array.from({ length: 5 }).map((_, i) => <TableRow key={i}>{Array.from({ length: 8 }).map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)
                      : services.map(row)}
                  </TableBody>
                </Table>
              </TableContainer>
              <CustomTablePagination
                count={total} page={page} rowsPerPage={rowsPerPage}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              />
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ComptoirServices;
